/* Provador virtual Ankor — motor de cálculo (sem interface).
 * Entradas: altura (cm), peso (kg), idade, formato do tórax, barriga, preferência de caimento.
 * 1) Estima as medidas do corpo com regressões ajustadas no ANSUR II (4.082 homens medidos).
 * 2) Compara com as medidas da peça (tabela da estilista) região por região.
 * 3) Recomenda o tamanho com menor "desconforto" ponderado.
 */
(function (global) {
  "use strict";

  const REGIOES = ["ombro", "peito", "cintura", "comprimento", "manga"];
  const NOME_REGIAO = { ombro: "Ombros", peito: "Peito", cintura: "Cintura", comprimento: "Comprimento", manga: "Mangas" };

  // ajustes pelo formato do corpo (cm)
  // aceita as opções em palavras (versão com avatar) ou escala 1–5 (versão da tabela)
  const AJ_TORAX = {
    estreito: { peito: -3, ombro: -1.2 }, medio: { peito: 0, ombro: 0 }, largo: { peito: 3.5, ombro: 1.4 },
    1: { peito: -5, ombro: -2 }, 2: { peito: -2.5, ombro: -1 }, 3: { peito: 0, ombro: 0 }, 4: { peito: 3, ombro: 1.2 }, 5: { peito: 6, ombro: 2.4 },
  };
  const AJ_BARRIGA = { chapada: -4, normal: 0, saliente: 6, 1: -6, 2: -3, 3: 0, 4: 4, 5: 9 };
  const AJ_PREF = { justo: -4, regular: 0, solto: 5, 1: -6, 2: -3, 3: 0, 4: 4, 5: 8 };
  /** Categoria de barriga (para escolher o avatar e o peso da cintura). */
  const tipoBarriga = (b) => (b == 1 || b == 2 ? "chapada" : b == 4 || b == 5 ? "saliente" : AJ_BARRIGA[b] !== undefined && isNaN(+b) ? b : "normal");

  // folga ideal (peça − corpo, em cm) por tipo de tecido
  const FAIXAS = {
    plano: { peito: [8, 18], cintura: [10, 24], ombro: [4, 10], comprimento: [2, 10], manga: [3, 10] },
    malha: { peito: [3, 13], cintura: [4, 16], ombro: [3, 9], comprimento: [2, 10], manga: [3, 10] },
  };
  const PESO = { ombro: 1.5, peito: 3, cintura: 2, comprimento: 0.7, manga: 0.7 };
  // modelagem da peça: desloca a folga ideal de peito/cintura (cm)
  const AJ_MODELAGEM = { justa: -3, regular: 0, ampla: 7 };

  /** Palpite de modelagem pela tabela: folga do tamanho do meio sobre um tórax de referência (100 cm). */
  function modelagemPelaTabela(produto) {
    const ts = produto.tamanhos.filter((t) => produto.med[t] && produto.med[t].peito);
    if (!ts.length) return "regular";
    const meio = produto.med[ts.indexOf("M") >= 0 ? "M" : ts[Math.floor((ts.length - 1) / 2)]];
    const folga = meio.peito - 100;
    const amplo = produto.tipo === "malha" ? 10 : 16;
    return folga >= amplo ? "ampla" : "regular";
  }

  function prever(modelo, nome, H, W, A) {
    const bmi = W / Math.pow(H / 100, 2);
    const x = [1, H, W, A, bmi, bmi * bmi, A * bmi, H * bmi];
    const c = modelo.models[nome].coef;
    let s = 0;
    for (let i = 0; i < c.length; i++) s += c[i] * x[i];
    return s;
  }

  const limitar = (v, a, b) => Math.min(Math.max(v, a), b);

  /** Medidas estimadas do corpo (cm). */
  function medirCorpo(modelo, p) {
    const H = limitar(+p.altura, 150, 200), W = limitar(+p.peso, 40, 160), A = limitar(+p.idade || 30, 17, 58);
    const t = AJ_TORAX[p.torax] || AJ_TORAX.medio;
    const corpo = {
      altura: H, peso: W, imc: W / Math.pow(H / 100, 2),
      peito: prever(modelo, "peito", H, W, A) + t.peito,
      cintura: prever(modelo, "cintura", H, W, A) + (AJ_BARRIGA[p.barriga] || 0),
      quadril: prever(modelo, "quadril", H, W, A),
      ombro: prever(modelo, "ombroOsso", H, W, A) + t.ombro,
      tronco: prever(modelo, "cervical", H, W, A) - prever(modelo, "virilha", H, W, A),
      braco: prever(modelo, "bracoOmbroPunho", H, W, A),
    };
    corpo.estimado = { peito: corpo.peito, cintura: corpo.cintura };
    // medidas que o cliente informou/ajustou têm prioridade sobre a estimativa
    const aj = p.medidas || {};
    if (aj.peito > 60 && aj.peito < 180) corpo.peito = +aj.peito;
    if (aj.cintura > 50 && aj.cintura < 180) corpo.cintura = +aj.cintura;
    return corpo;
  }

  /** "S | P" (tricots: internacional | BR) vira "P"; o resto fica igual. */
  function nomeTamanho(t) {
    const s = String(t).trim();
    return s.includes("|") ? s.split("|").pop().trim() : s;
  }

  /** Quando a tabela usa outra grade (ex.: 1, 2, 3, 4) e a loja vende P, M, G, GG, casa pela ordem. */
  function alinharGrade(med, tamanhosLoja) {
    const chaves = Object.keys(med);
    if (!tamanhosLoja || !tamanhosLoja.length) return med;
    const achados = tamanhosLoja.filter((t) => med[t]).length;
    if (achados * 2 > tamanhosLoja.length) return med;
    if (chaves.length < tamanhosLoja.length) return med;
    const ORDEM = ["PP", "P", "M", "G", "GG", "XG", "XGG", "EG", "EGG"];
    const pos = (t) => (ORDEM.indexOf(t) < 0 ? 99 : ORDEM.indexOf(t));
    const loja = tamanhosLoja.slice().sort((a, b) => pos(a) - pos(b));
    const out = {};
    loja.forEach((t, i) => { out[t] = med[t] || med[chaves[i]]; });
    return out;
  }

  /** Converte uma entrada de size-charts-full.json (formato do tema) nas medidas usadas aqui. */
  function lerTabela(tabela) {
    const pega = (vals, nomes) => {
      for (const n of nomes) for (const k in vals) if (k.toUpperCase() === n) {
        const v = parseFloat(String(vals[k]).replace(",", "."));
        if (!isNaN(v)) return v;
      }
      return null;
    };
    const med = {};
    for (const r of tabela.rows || []) {
      const v = r.values || {};
      let ombro = pega(v, ["OMBRO A OMBRO", "OMBRO"]);
      if (ombro !== null && ombro < 30) ombro = null; // "OMBRO" 12–14 cm é a costura, não ombro a ombro
      const torax = pega(v, ["TORAX", "TÓRAX", "PEITO (TÓRAX)", "PEITO"]);
      const cint = pega(v, ["CINTURA"]);
      med[nomeTamanho(r.size)] = {
        peito: torax && torax * 2,
        cintura: cint && cint * 2,
        ombro: ombro,
        comp: pega(v, ["COMPRIMENTO TOTAL", "COMPRIMENTO"]),
        manga: pega(v, ["COMPRIMENTO MANGA", "COMP. MANGA", "COMP MANGAS", "MANGA"]),
      };
    }
    return med;
  }

  // −2 justo/curto · −1 levemente · 0 ideal · +1 levemente folgado/longo · +2 folgado/longo
  function nivel(folga, faixa) {
    const [lo, hi] = faixa;
    if (folga < lo - 5) return -2;
    if (folga < lo) return -1;
    if (folga <= hi) return 0;
    if (folga <= hi + 6) return 1;
    return 2;
  }

  /** Posição contínua 0–1 na régua Justo → Folgado (cinco trechos de 20%). */
  function posicao(folga, [lo, hi]) {
    const pts = [[lo - 15, 0], [lo - 5, 0.2], [lo, 0.4], [hi, 0.6], [hi + 6, 0.8], [hi + 16, 1]];
    if (folga <= pts[0][0]) return 0.02;
    for (let i = 1; i < pts.length; i++) {
      if (folga <= pts[i][0]) {
        const [x0, y0] = pts[i - 1], [x1, y1] = pts[i];
        return Math.min(0.98, Math.max(0.02, y0 + ((folga - x0) / (x1 - x0)) * (y1 - y0)));
      }
    }
    return 0.98;
  }

  function avaliarTamanho(corpo, m, tipo, pref, barriga, modelagem) {
    const F = FAIXAS[tipo] || FAIXAS.plano;
    const d = (AJ_PREF[pref] || 0) + (AJ_MODELAGEM[modelagem] || 0);
    const res = {};
    const add = (reg, folga, faixa) => {
      if (folga === null || isNaN(folga)) return;
      res[reg] = { folga: Math.round(folga * 10) / 10, nivel: nivel(folga, faixa), faixa, pos: posicao(folga, faixa) };
    };
    if (m.peito) add("peito", m.peito - corpo.peito, [F.peito[0] + d, F.peito[1] + d]);
    const cint = m.cintura || m.peito; // camisetas retas: sem cintura na tabela, usa o tórax
    if (cint) add("cintura", cint - corpo.cintura, [F.cintura[0] + d, F.cintura[1] + d]);
    if (m.ombro) add("ombro", m.ombro - corpo.ombro, F.ombro);
    if (m.comp) add("comprimento", m.comp - corpo.tronco, F.comprimento);
    if (m.manga && m.manga > 40) add("manga", m.manga - corpo.braco, F.manga); // só manga longa
    let pontos = 0;
    for (const reg in res) {
      const { folga, faixa } = res[reg];
      const fora = folga < faixa[0] ? (faixa[0] - folga) * 1.6 : folga > faixa[1] ? folga - faixa[1] : 0;
      const peso = reg === "cintura" && tipoBarriga(barriga) === "saliente" ? 3 : PESO[reg];
      pontos += peso * fora;
    }
    return { regioes: res, pontos };
  }

  /** Recomendação completa para um produto. */
  function recomendar(modelo, perfil, produto) {
    const corpo = medirCorpo(modelo, perfil);
    const tamanhos = produto.tamanhos.filter((t) => produto.med[t]);
    const modelagem = produto.modelagem || modelagemPelaTabela(produto);
    const porTamanho = {};
    for (const t of tamanhos) porTamanho[t] = avaliarTamanho(corpo, produto.med[t], produto.tipo, perfil.pref, perfil.barriga, modelagem);
    const ordem = tamanhos.slice().sort((a, b) => porTamanho[a].pontos - porTamanho[b].pontos);
    const melhor = ordem[0];
    let alternativa = null;
    if (ordem[1] && porTamanho[ordem[1]].pontos - porTamanho[melhor].pontos < 2.5) alternativa = ordem[1];
    // fora da grade: até o maior/menor tamanho fica justo/folgado demais no peito
    const pm = porTamanho[melhor].regioes.peito;
    const foraDaGrade = pm && Math.abs(pm.nivel) === 2;
    const margem = ordem[1] ? porTamanho[ordem[1]].pontos - porTamanho[melhor].pontos : 99;
    const confianca = foraDaGrade ? "baixa" : margem > 6 ? "alta" : margem > 2.5 ? "boa" : "media";
    return { corpo, porTamanho, tamanhos, melhor, alternativa, foraDaGrade, modelagem, confianca };
  }

  /** Avatar mais parecido pelas medidas (tórax e cintura) — usado quando o cliente ajusta os valores. */
  function avatarPorMedidas(lista, corpo) {
    let best = null, dist = Infinity;
    for (const id in lista) {
      const a = lista[id];
      const d = Math.hypot((a.peito - corpo.peito) * 0.8, a.cintura - corpo.cintura);
      if (d < dist) { dist = d; best = id; }
    }
    return best;
  }

  /** Escolhe o avatar renderizado mais parecido (IMC × barriga). */
  function escolherAvatar(lista, corpo, barriga) {
    const ids = Object.keys(lista);
    if (ids.length === 1) return ids[0]; // manequim padrão único
    let best = null, dist = Infinity;
    for (const id in lista) {
      const m = /^imc([\d_]+)-(\w+)$/.exec(id);
      if (!m || m[2] !== tipoBarriga(barriga)) continue;
      const imc = parseFloat(m[1].replace("_", "."));
      const dd = Math.abs(imc - corpo.imc);
      if (dd < dist) { dist = dd; best = id; }
    }
    return best;
  }

  const TEXTO_NIVEL = {
    padrao: { "-2": "Justo", "-1": "Levemente justo", "0": "Ideal", "1": "Levemente folgado", "2": "Folgado" },
    comprimento: { "-2": "Curto", "-1": "Levemente curto", "0": "Ideal", "1": "Levemente longo", "2": "Longo" },
    manga: { "-2": "Curta", "-1": "Levemente curta", "0": "Ideal", "1": "Levemente longa", "2": "Longa" },
  };
  const textoNivel = (reg, n) => (TEXTO_NIVEL[reg] || TEXTO_NIVEL.padrao)[String(n)];

  global.ProvadorMotor = { nomeTamanho, alinharGrade, avatarPorMedidas, tipoBarriga, modelagemPelaTabela, REGIOES, NOME_REGIAO, medirCorpo, lerTabela, recomendar, escolherAvatar, textoNivel };
})(typeof window !== "undefined" ? window : globalThis);
